import { useEffect, useState, type JSX } from 'react'
import { SlidersHorizontal, Trash2 } from 'lucide-react'
import ResourceDrawer from '../../ResourceDrawer.js'
import Select from '../../../ui/Select.js'
import {
  DRAWER_INPUT_CLASS,
  DRAWER_LABEL_CLASS,
  DRAWER_HINT_CLASS,
} from '../versioned-config-ui.js'

interface ModelLimitDrawerProps {
  open: boolean
  onClose: () => void
  onSave: (
    model: string,
    monthlyCapUsd: number,
    action: 'block' | 'degrade',
    targetModel?: string | null | undefined,
  ) => Promise<void>
  onDelete?: ((model: string) => Promise<void>) | undefined
  model: string
  currentCapUsd?: number | undefined
  currentAction?: 'block' | 'degrade' | undefined
  currentTargetModel?: string | null | undefined
  configuredModels?: string[] | undefined
  saving?: boolean | undefined
}

export default function ModelLimitDrawer({
  open,
  onClose,
  onSave,
  onDelete,
  model,
  currentCapUsd = 50,
  currentAction = 'block',
  currentTargetModel = null,
  configuredModels = [],
  saving = false,
}: ModelLimitDrawerProps): JSX.Element {
  const [capUsd, setCapUsd] = useState(String(currentCapUsd || ''))
  const [action, setAction] = useState<'block' | 'degrade'>(currentAction)
  const [targetModel, setTargetModel] = useState<string>(currentTargetModel ?? '')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setCapUsd(currentCapUsd ? String(currentCapUsd) : '50')
    setAction(currentAction)
    setTargetModel(currentTargetModel ?? '')
  }, [model, currentCapUsd, currentAction, currentTargetModel, open])

  const parsedCap = parseFloat(capUsd)
  const valid = !Number.isNaN(parsedCap) && parsedCap > 0

  async function handleSave(): Promise<void> {
    if (!valid) return
    await onSave(
      model,
      parsedCap,
      action,
      action === 'degrade' && targetModel.trim() ? targetModel.trim() : null,
    )
  }

  async function handleDelete(): Promise<void> {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete(model)
      onClose()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={handleSave}
      title={`Set Monthly Cap: ${model}`}
      icon={<SlidersHorizontal className="h-4 w-4 text-white" />}
      width="standard"
      saving={saving || deleting}
      disabled={!valid}
      saveLabel="Save Limit"
    >
      <div className="flex flex-col gap-5 py-2">
        <div>
          <label className={DRAWER_LABEL_CLASS}>Target Model</label>
          <div className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800">
            {model}
          </div>
          <span className={DRAWER_HINT_CLASS}>
            Spend for this model is metered independently of the global cap.
          </span>
        </div>

        <div>
          <label className={DRAWER_LABEL_CLASS}>Monthly Cap ($ USD)</label>
          <input
            type="number"
            min="1"
            step="1"
            value={capUsd}
            onChange={(e) => setCapUsd(e.target.value)}
            placeholder="e.g. 50.00"
            className={DRAWER_INPUT_CLASS}
            autoFocus
          />
          <span className={DRAWER_HINT_CLASS}>
            Maximum spend permitted for this specific model in a billing cycle.
          </span>
        </div>

        <div>
          <label className={DRAWER_LABEL_CLASS}>Action When Cap Exceeded</label>
          <Select
            ariaLabel="Action when model cap is exceeded"
            value={action}
            onChange={(v) => setAction(v as 'block' | 'degrade')}
          >
            <option value="block">Block all requests to this model</option>
            <option value="degrade">Degrade / Fallback to another configured model</option>
          </Select>
          <span className={DRAWER_HINT_CLASS}>
            {action === 'block'
              ? 'Requests will be rejected once this model hits its monthly cap.'
              : 'Requests will be rerouted to a cheaper or free configured model.'}
          </span>
        </div>

        {action === 'degrade' && (
          <div>
            <label className={DRAWER_LABEL_CLASS}>Fallback Target Model</label>
            <Select
              ariaLabel="Fallback target model"
              value={targetModel}
              onChange={(v) => setTargetModel(v)}
            >
              <option value="">Default (Fast Cloud / Free)</option>
              {configuredModels
                .filter((m) => m !== model)
                .map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
            </Select>
            <span className={DRAWER_HINT_CLASS}>
              Requests will automatically route to this model when the cap trips.
            </span>
          </div>
        )}

        {currentCapUsd !== undefined && currentCapUsd > 0 && onDelete && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove Monthly Cap
            </button>
          </div>
        )}
      </div>
    </ResourceDrawer>
  )
}
